import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import {
  Eye,
  EyeOff,
  Building,
  User,
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Check,
  Mail,
  Calendar,
  Shield,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Icon } from "@iconify/react"; // npm i @iconify/react
import InputField from "./components/InputComponent/InputField";
import axios from "axios";
import ilaLogo from "./assets/ila-logo.png";
import ourDeptLogo from "./assets/ourdept-logo.png";
import "./Login.scss";

/* Products shown in the top launcher come from
   GET {BACKEND}/api/public/products/login-page/all
   Response: { status, message, data: [{ productName, description, productCode,
   productLink, productIconUrl, productType }] }
   The API has no colour fields, so each product gets a stable accent colour
   derived from its productCode. Works for any number of products. */
const PRODUCTS_URL = "/api/public/products/login-page/all";

const hashString = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
};

// Golden-angle hue spacing keeps neighbouring products visually distinct.
const productColors = (key) => {
  const hue = (hashString(key) * 137.508) % 360;
  return {
    color: `hsl(${hue.toFixed(0)} 65% 42%)`,
    softColor: `hsl(${hue.toFixed(0)} 85% 93%)`,
  };
};

const normalizeHost = (host = "") => host.toLowerCase().replace(/^www\./, "");

const hostOf = (url = "") => {
  try {
    return normalizeHost(new URL(url, window.location.origin).hostname);
  } catch {
    return "";
  }
};

// True when the product is the app the user is currently on. Matches either
// the product's own code (REACT_APP_PRODUCT_ID, the same value sent as
// X-ProductID on login) or its link's hostname vs. the current URL.
const isCurrentProduct = (product) => {
  const currentCode = process.env.REACT_APP_PRODUCT_ID;
  if (currentCode && product.productCode === currentCode) return true;
  const productHost = hostOf(product.productLink);
  return (
    !!productHost && productHost === normalizeHost(window.location.hostname)
  );
};

const isImageUrl = (value = "") =>
  /^(https?:)?\/\/|^\/|^data:image\//i.test(value);

function ProductIcon({ product }) {
  const { productIconUrl, productName } = product;
  if (productIconUrl && isImageUrl(productIconUrl)) {
    return (
      <img className="lg-app__img" src={productIconUrl} alt="" loading="lazy" />
    );
  }
  if (productIconUrl) {
    // Iconify id, e.g. "icon-park:setting-two"
    return <Icon icon={productIconUrl} width="22" height="22" />;
  }
  // No icon supplied: fall back to the first letter of the name.
  return (
    <span className="lg-app__letter">
      {(productName || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
}

export default function ModernLoginPage() {
  // 👈 REMOVED onLogin PROP
  const { login, setJWTToken } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("login");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    tenantId: "",
    username: "",
    password: "",
    email: "",
    dob: "",
    newPassword: "",
    confirmPassword: "",
    otp: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const employeeSlides = [
    {
      title: "Are you an Employee?",
      content: "Hey there! Back for more hassle-free HR?",
      subtitle: "Everything you need — one click away.",
      features: [
        "🧾 View and download your payslips",
        "🗓 Apply for leave easily",
        "🤝 Stay connected with your team",
      ],
      footer: "HR that works for you. No chasing. No confusion. Just clarity.",
      visual: "payslip",
    },
  ];

  const hrSlides = [
    {
      title: "Are you HR?",
      content: "✨ Log Back In — Your Team's Potential is Waiting",
      features: [
        "⚙️ Automate repetitive HR tasks",
        "📊 Monitor employee performance",
        "📅 Manage leave and schedules",
        "🛡 Ensure compliance and security",
      ],
      footer: "Thousands of HR leaders already trust us.",
      subtitle: "Login to unlock complete HR efficiency.",
      visual: "analytics",
    },
    {
      title: "Ask a Question",
      content: "Need help? Our support team is here for you 24/7.",
      icon: "💬",
      visual: "chat",
    },
  ];

  const currentSlides = [...employeeSlides, ...hrSlides];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Product launcher: fetched once on mount.
  useEffect(() => {
    const controller = new AbortController();

    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}${PRODUCTS_URL}`,
          { signal: controller.signal },
        );
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setProducts(
          list
            .filter((p) => p && p.productName && !isCurrentProduct(p))
            .map((p) => ({
              ...p,
              ...productColors(p.productCode || p.productName),
            })),
        );
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        console.error("Failed to load products:", err.message);
        setProducts([]); // launcher simply stays hidden; login still works
      } finally {
        if (!controller.signal.aborted) setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
    }, 7000);
    return () => clearTimeout(timer);
  }, [currentSlide, currentSlides.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearMessages();

    const { username, password, tenantId } = formData;

    if (!username || !password || !tenantId) {
      setError("Company ID, Username, and Password are required");
      return;
    }

    setLoading(true);

    const credentials = `${username}:${password}`;
    const token = btoa(credentials);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/employeeLogin`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${token}`,
            "X-TenantID": tenantId,
            "X-ProductID": process.env.REACT_APP_PRODUCT_ID,
          },
          credentials: "include", // Important for httpOnly cookies
        },
      );

      if (!response.ok) {
        if (response.status === 409) {
          const jwtToken = response.headers.get("Authorization");
          const tenantIdFromResponse = sessionStorage.getItem("X-TenantID");

          // Store JWT token and tenantId directly in sessionStorage
          if (jwtToken) {
            sessionStorage.setItem("jwtToken", jwtToken);
          }

          // ✅ FIX: Store the tenantId from user input
          sessionStorage.setItem("tenantId", tenantId);

          const errorData = await response.json();
          throw new Error(errorData.message || "Conflict occurred");
        } else if (response.status === 401 || response.status === 400) {
          throw new Error("Invalid credentials");
        } else {
          throw new Error("Something went wrong. Please try again.");
        }
      }

      const data = await response.json();

      if (data) {
        // Check if JWT token is in response headers
        const jwtToken = response.headers.get("Authorization");
        const tenantIdFromResponse = response.headers.get("X-TenantID");

        // Store JWT token and tenantId directly in sessionStorage
        if (jwtToken) {
          sessionStorage.setItem("jwtToken", jwtToken);
        }

        // ✅ FIX: Store the tenantId from user input
        sessionStorage.setItem("tenantId", tenantId);

        // Set user data in AuthContext (without JWT token)
        login(data);

        setSuccess("Login successful!");

        // Navigate to the dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      } else {
        setError("Invalid credentials or unexpected response.");
      }
    } catch (error) {
      console.error("Error:", error.message);
      setError(error.message || "Login failed, please try again later");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    clearMessages();

    const { email, tenantId, dob, newPassword, confirmPassword } = formData;

    if (!email || !tenantId || !dob || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password`,
        {
          email,
          currentPassword: "",
          newPassword,
          companyId: tenantId,
          dob,
        },
        {
          headers: {
            "X-TenantID": tenantId,
          },
        },
      );

      if (response.status === 200) {
        setSuccess(
          "Password changed successfully! Please login with your new password.",
        );
        setTimeout(() => {
          switchToLogin();
        }, 2000);
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to update password. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setFormData((prev) => ({
      ...prev,
      email: "",
      dob: "",
      newPassword: "",
      confirmPassword: "",
    }));
  };

  const switchToForgotPassword = () => {
    clearMessages();
    setActiveTab("forgot");
    resetForgotPassword();
  };

  const switchToLogin = () => {
    clearMessages();
    setActiveTab("login");
    resetForgotPassword();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % currentSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + currentSlides.length) % currentSlides.length,
    );
  };

  const renderScene = (type) => {
    if (type === "payslip")
      return (
        <div className="win">
          <div className="win__bar">
            <i />
            <i />
            <i />
            <span>My Payslip</span>
          </div>
          <div className="win__body win__body--pay">
            <div className="card">
              <div className="pay__who">
                <b className="av" />
                <div>
                  <i className="sk" style={{ "--w": "70%" }} />
                  <i className="sk" style={{ "--w": "40%" }} />
                </div>
              </div>
              {[0, 1, 2].map((n) => (
                <div className="pay__row" key={n}>
                  <i className="sk" style={{ "--w": "38%" }} />
                  <i className="sk" style={{ "--w": "22%" }} />
                </div>
              ))}
              <i className="sk sk--a" style={{ "--w": "80%", marginTop: 12 }} />
            </div>
            <div className="card">
              <i className="sk" style={{ "--w": "50%" }} />
              <div className="cal__grid">
                {Array.from({ length: 28 }).map((_, n) => (
                  <span
                    key={n}
                    className={`d ${[9, 10, 16, 17].includes(n) ? "ok" : ""} ${[23, 24].includes(n) ? "wait" : ""}`}
                    style={{ "--n": n }}
                  />
                ))}
              </div>
            </div>
            <div className="toast">
              <Check /> Leave approved
            </div>
          </div>
        </div>
      );
    if (type === "analytics")
      return (
        <div className="win">
          <div className="win__bar">
            <i />
            <i />
            <i />
            <span>Team Dashboard</span>
          </div>
          <div className="win__body win__body--hr">
            {[
              ["#4f46e5", "M0 24 L20 18 L40 21 L60 10 L80 13 L100 3"],
              ["#059669", "M0 20 L25 22 L45 12 L70 15 L100 5"],
              ["#ea580c", "M0 12 L20 16 L45 8 L70 18 L100 6"],
            ].map(([c, d], n) => (
              <div className="card kpi" key={n} style={{ "--c": c, "--n": n }}>
                <i className="sk" style={{ "--w": "55%" }} />
                <svg viewBox="0 0 100 30" preserveAspectRatio="none">
                  <path d={d} pathLength="1" />
                </svg>
              </div>
            ))}
            <div className="card chart">
              <div className="bars">
                {[40, 62, 50, 78, 66, 94].map((h, n) => (
                  <span key={n} style={{ "--h": `${h}%`, "--n": n }} />
                ))}
              </div>
            </div>
            <div className="card donut">
              <svg viewBox="0 0 100 100">
                <circle className="bg" cx="50" cy="50" r="40" />
                <circle className="fg" cx="50" cy="50" r="40" />
              </svg>
            </div>
            <div className="toast">
              <Check /> Goal achieved
            </div>
          </div>
        </div>
      );
    return (
      <div className="win">
        <div className="win__bar">
          <i />
          <i />
          <i />
          <span>Help Desk</span>
        </div>
        <div className="win__body win__body--chat">
          <div className="card list">
            {[0, 1, 2].map((n) => (
              <div className={`li ${n === 0 ? "on" : ""}`} key={n}>
                <b className="av" />
                <div>
                  <i className="sk" style={{ "--w": "80%" }} />
                  <i className="sk" style={{ "--w": "50%" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="card thread">
            <p className="q">How do I apply for leave?</p>
            <div className="typing">
              <i />
              <i />
              <i />
            </div>
            <p className="a">Open Leave, pick your dates. Done.</p>
            <div className="input">
              <i className="sk" style={{ "--w": "45%" }} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`lg-page ${isVisible ? "lg-page--in" : ""}`}>
      {/* ---------- Top: software launcher ---------- */}
      <header className="lg-top">
        {(productsLoading || products.length > 0) && (
          <nav className="lg-apps__grid" aria-label="Your software">
            {productsLoading
              ? [0, 1, 2, 3].map((n) => (
                  <span
                    key={n}
                    className="lg-app lg-app--skeleton"
                    aria-hidden="true"
                  >
                    <span className="lg-app__icon" />
                    <i className="sk" style={{ "--w": "60%", margin: 0 }} />
                  </span>
                ))
              : products.map((p, i) => (
                  <a
                    key={p.productCode || p.productName}
                    href={p.productLink}
                    title={p.description || p.productName}
                    className="lg-app lg-reveal"
                    style={{
                      "--i": Math.min(i, 8),
                      "--c": p.color,
                      "--soft": p.softColor,
                    }}
                  >
                    <span className="lg-app__icon">
                      <ProductIcon product={p} />
                    </span>
                    <span className="lg-app__name">{p.productName}</span>
                    <ArrowUpRight className="lg-app__go w-4 h-4" />
                  </a>
                ))}
          </nav>
        )}
      </header>

      <main className="lg-main">
        {/* ---------- Left: sign in / reset ---------- */}
        <section className="lg-left lg-reveal" style={{ "--i": 4 }}>
          <div className="lg-left__body">
            <div className="lg-pane" key={activeTab}>
              {activeTab !== "forgot" ? (
                <>
                  <img
                    className="lg-brand"
                    src={ourDeptLogo}
                    alt="OurDept.com - One platform. Every department. Total control."
                  />
                  <h2>Welcome Back</h2>
                  <p className="lg-sub">Enter your credentials to continue</p>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  <form className="lg-fields" onSubmit={handleSubmit}>
                    <InputField
                      label="Company ID"
                      type="text"
                      value={formData.tenantId}
                      onChange={(e) =>
                        handleInputChange("tenantId", e.target.value)
                      }
                      placeholder="Enter your company ID"
                      icon={Building}
                    />
                    <InputField
                      label="Username"
                      type="text"
                      value={formData.username}
                      onChange={(e) =>
                        handleInputChange("username", e.target.value)
                      }
                      placeholder="Enter your username"
                      icon={User}
                    />
                    <div className="space-y-2">
                      <label>Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          aria-label="Toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="lg-btn">
                      {loading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={switchToForgotPassword}
                        className="lg-link"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {error && (
                    <div className="lg-msg bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="lg-msg bg-emerald-50 border border-emerald-200 text-emerald-700 p-3 rounded-xl mb-4 text-center text-sm">
                      {success}
                    </div>
                  )}
                  <form className="lg-fields" onSubmit={handleForgotPassword}>
                    <div className="lg-row2">
                      <InputField
                        label="Company ID"
                        type="text"
                        value={formData.tenantId}
                        onChange={(e) =>
                          handleInputChange("tenantId", e.target.value)
                        }
                        placeholder="Company ID"
                        icon={Building}
                      />
                      <InputField
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        placeholder="Email address"
                        icon={Mail}
                      />
                    </div>
                    <InputField
                      label="Date of Birth"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => handleInputChange("dob", e.target.value)}
                      icon={Calendar}
                    />
                    <div className="lg-row2">
                      <div className="space-y-2">
                        <label>New Password</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={formData.newPassword}
                            onChange={(e) =>
                              handleInputChange("newPassword", e.target.value)
                            }
                            className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                            placeholder="New password"
                          />
                          <button
                            type="button"
                            aria-label="Toggle password visibility"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showNewPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label>Confirm Password</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={(e) =>
                              handleInputChange(
                                "confirmPassword",
                                e.target.value,
                              )
                            }
                            className="w-full pl-3 pr-11 py-2.5 text-slate-800 placeholder-slate-400"
                            placeholder="Confirm password"
                          />
                          <button
                            type="button"
                            aria-label="Toggle password visibility"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="lg-btn">
                      {loading ? "Processing..." : "Reset Password"}
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={switchToLogin}
                        className="lg-link inline-flex items-center"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Sign In
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
          {/* ---------- Footer: animated logo (under the left column only) ---------- */}
          <footer className="lg-foot">
            <a
              className="lg-logo"
              href="https://www.indialeadershipawards.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="India Leadership Awards"
              style={{ "--logo": `url(${ilaLogo})` }}
            >
              <span className="lg-logo__glow" aria-hidden="true" />
              <span className="lg-logo__art">
                <img src={ilaLogo} alt="India Leadership Awards" />
                <span className="lg-logo__shine" aria-hidden="true" />
                <span
                  className="lg-star"
                  style={{ "--x": "27%", "--y": "4%", "--d": "0s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "31.2%", "--y": "8%", "--d": "0.9s" }}
                />
                <span
                  className="lg-star"
                  style={{ "--x": "23.2%", "--y": "8%", "--d": "1.7s" }}
                />
              </span>
              {[8, 20, 32, 44, 56, 68, 80, 92].map((x, i) => (
                <span
                  key={x}
                  className="lg-spark"
                  style={{ "--x": `${x}%`, "--d": `${(i * 0.75) % 5}s` }}
                />
              ))}
            </a>
          </footer>
        </section>

        {/* ---------- Right: product preview ---------- */}
        <aside className="lg-right lg-reveal" style={{ "--i": 5 }}>
          {activeTab === "forgot" ? (
            <div className="lg-recover">
              <div className="lg-shield">
                <Shield className="w-10 h-10" />
              </div>
              <h1>Secure Recovery</h1>
              <p>
                We'll help you regain access to your account safely and securely
              </p>
              <div className="lg-note">
                Fill in all the required details to reset your password
                instantly.
              </div>
            </div>
          ) : (
            <>
              <div className="lg-slide" key={currentSlide}>
                <div className="lg-copy">
                  <h2>{currentSlides[currentSlide].title}</h2>
                  <p className="lead">{currentSlides[currentSlide].content}</p>
                  {currentSlides[currentSlide].subtitle && (
                    <p>{currentSlides[currentSlide].subtitle}</p>
                  )}
                  {currentSlides[currentSlide].features && (
                    <ul className="lg-feats">
                      {currentSlides[currentSlide].features.map(
                        (feature, index) => (
                          <li key={index} style={{ "--n": index }}>
                            <Check />
                            {feature}
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                  {currentSlides[currentSlide].footer && (
                    <p className="lg-foot-note">
                      {currentSlides[currentSlide].footer}
                    </p>
                  )}
                </div>
                <div className="lg-winwrap">
                  {renderScene(currentSlides[currentSlide].visual)}
                </div>
              </div>
              <div className="lg-ctrl">
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={prevSlide}
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="lg-dots">
                  {currentSlides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Go to slide ${index + 1}`}
                      onClick={() => setCurrentSlide(index)}
                      className={`lg-dot ${index === currentSlide ? "is-active" : ""}`}
                    >
                      <span />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="lg-arrow"
                  onClick={nextSlide}
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </aside>
      </main>
    </div>
  );
}
